import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  LinearProgress,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import EventNoteIcon from '@mui/icons-material/EventNoteRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import toast from 'react-hot-toast';

import { actionPlanApi } from '../../api/actionPlanApi';
import {
  QUESTIONNAIRE_TYPES,
  formatDateRange
} from '../../components/dashboard/questionnaire/responses/constants';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey, lightVeil, greyAlpha } from '../../theme/tones';

/** Ce qu'il reste à faire sur la session, en trois mots. */
const stateLabel = (session) => {
  if (session.state === 'none') return 'Plan à écrire';
  if (session.total_rows > 1) return `${session.closed_rows}/${session.total_rows} lignes clôturées`;
  return 'Plan non clôturé';
};

/**
 * Suivi transversal des plans d'action : les sessions couvertes par un
 * questionnaire de satisfaction dont le plan n'est pas clôturé.
 *
 * L'unité est la SESSION — chacune a son plan, qu'on clôture quand tout est
 * traité. Une session peut porter plusieurs lignes (les remarques des
 * apprenants d'un côté, les écarts pré/post de l'autre) : elle n'est clôturée
 * que lorsque toutes le sont, et la carte montre où elle en est. Une session
 * couverte mais sans aucune ligne compte comme non clôturée : son plan reste
 * à écrire.
 *
 * Deux liens par session : la fiche session (le terrain) et le plan d'action de
 * la formation (là où la clôture se saisit).
 */
const ActionPlansOuverts = () => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Les compteurs du bandeau servent de filtres : cliquer sur « 3 sans plan
  // ecrit » montre ces trois sessions, recliquer rend la liste entiere.
  const [filtre, setFiltre] = useState('tous');
  // Les lignes rattachees a la formation entiere : annoncees par un compteur,
  // elles n'etaient affichees nulle part.
  const [orphans, setOrphans] = useState([]);

  const accent = getTone('amber').base;

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      const accessToken = window.localStorage.getItem('accessToken');
      const res = await actionPlanApi.getOpenPlans(accessToken, signal);
      if (res && res.status) {
        setSessions(res.sessions || []);
        setOrphans(res.orphans || []);
        setStats(res.stats || null);
      } else if (res?.message) {
        toast.error(res.message);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(err);
        toast.error('Suivi des plans d’action indisponible');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    // Le filtre du bandeau d'abord, la recherche ensuite : les deux se
    // cumulent, pour pouvoir chercher une formation parmi les sessions sans
    // plan ecrit.
    let liste = sessions;
    if (filtre === 'sans_plan') {
      liste = liste.filter((session) => session.state === 'none');
    } else if (filtre === 'partiels') {
      liste = liste.filter((session) => session.state === 'partial');
    } else if (filtre === 'ecrits') {
      liste = liste.filter((session) => session.state !== 'none');
    } else if (filtre === 'orphelines') {
      // Ces lignes ne relevent d'aucune session : elles s'affichent a part.
      liste = [];
    }

    if (!query) return liste;

    return liste.filter((session) => {
      const haystack = [
        session.formation_name,
        session.session_number,
        ...session.rows.map((r) => r.improvement_point)
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [sessions, search, filtre]);

  /** Les orphelines, filtrees par la meme recherche. */
  const orphelinesVues = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return orphans;
    return orphans.filter((row) => [row.improvement_point, row.theme, row.action_taken]
      .join(' ').toLowerCase().includes(query));
  }, [orphans, search]);

  /** Bascule un filtre : recliquer sur le compteur actif le retire. */
  const basculer = (cle) => setFiltre((actuel) => (actuel === cle ? 'tous' : cle));

  /** L'allure d'un compteur cliquable, selon qu'il est actif ou non. */
  const styleFiltre = (cle, tone) => ({
    bgcolor: filtre === cle ? getTone(tone).base : toneAlpha(tone, 0.16),
    color: filtre === cle ? grey(0) : getTone(tone).text,
    fontWeight: 800,
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 120ms',
    '&:hover': { bgcolor: filtre === cle ? getTone(tone).base : toneAlpha(tone, 0.28) }
  });

  const closedRatio = stats && stats.total > 0 ? (stats.closed / stats.total) * 100 : 0;

  return (
    <>
      <Helmet>
        <title>Plans d’action à clôturer | Qualité | Apteed</title>
      </Helmet>

      <Box sx={{ bgcolor: 'action.hover', minHeight: '100%', py: 3 }}>
        <Container maxWidth={false} disableGutters sx={{ px: 2 }}>

          {/* ── En-tête ───────────────────────────────────────────── */}
          <Box
            sx={{
              mb: 2.5,
              p: 3,
              borderRadius: '24px',
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              background: `linear-gradient(135deg, ${lightVeil(0.96)} 0%, ${greyAlpha(100, 0.75)} 100%)`,
              boxShadow: (theme) => `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0 }}>
              <Box
                sx={{
                  width: 54, height: 54, borderRadius: '16px', flexShrink: 0,
                  background: `linear-gradient(135deg, ${getTone('amber').light} 0%, ${getTone('amber').base} 100%)`,
                  display: 'grid', placeItems: 'center',
                  boxShadow: `0 8px 20px ${accent}4D`
                }}
              >
                <AssignmentIcon sx={{ color: grey(0), fontSize: '1.6rem' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.45rem', color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  Plans d’action à clôturer
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.87rem', mt: 0.5 }}>
                  Les sessions dont le plan d’action n’est pas encore clôturé.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Formation, session, point à améliorer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'background.paper' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  )
                }}
              />
              <Button
                component={RouterLink}
                to="/dashboard/questionnaire-satisfaction"
                startIcon={<ArrowLeftIcon style={{ fontSize: 13 }} />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}
              >
                Retour aux résultats
              </Button>
            </Box>
          </Box>

          {/* ── Compteur ──────────────────────────────────────────── */}
          {!loading && stats && (
            <Card
              sx={{
                p: 2.25,
                mb: 2.5,
                borderRadius: '18px',
                border: `1px solid ${grey(100)}`,
                boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                flexWrap: 'wrap'
              }}
            >
              <Box sx={{ minWidth: 220 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em', color: 'text.disabled', textTransform: 'uppercase' }}>
                  Sessions au plan d’action clôturé
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '1.9rem', fontWeight: 900, color: getTone('emerald').base, lineHeight: 1.1 }}>
                    {stats.closed}
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: 'text.disabled' }}>
                    / {stats.total}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, closedRatio))}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: greyAlpha(400, 0.18),
                    '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: getTone('emerald').base }
                  }}
                />
                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 700, mt: 0.75 }}>
                  {`${Math.round(closedRatio)} % des sessions couvertes par un questionnaire de satisfaction ont clôturé leur plan`}
                </Typography>
              </Box>

              <Tooltip title={filtre === 'tous'
                ? 'Toutes les sessions ouvertes sont affichées'
                : 'Revenir à la liste entière'}
              >
                <Chip
                  label={`${stats.open} session${stats.open > 1 ? 's' : ''} à clôturer`}
                  onClick={() => setFiltre('tous')}
                  sx={styleFiltre('tous', 'amber')}
                />
              </Tooltip>
              {stats.no_plan > 0 && (
                <Tooltip title="Sessions qui ont des réponses mais dont le plan d’action n’a pas encore été écrit — cliquez pour n’afficher qu’elles">
                  <Chip
                    label={`${stats.no_plan} sans plan écrit`}
                    size="small"
                    onClick={() => basculer('sans_plan')}
                    sx={styleFiltre('sans_plan', 'rose')}
                  />
                </Tooltip>
              )}
              <Tooltip title="Une session peut porter plusieurs lignes : les remarques des apprenants d’un côté, les écarts pré/post de l’autre. Cliquez pour n’afficher que les sessions dont le plan est écrit">
                <Chip
                  label={`${stats.rows_closed}/${stats.rows_total} lignes`}
                  size="small"
                  onClick={() => basculer('ecrits')}
                  sx={styleFiltre('ecrits', 'neutral')}
                />
              </Tooltip>

              {stats.partial > 0 && (
                <Tooltip title="Plans commencés : une partie des lignes est clôturée, le reste attend">
                  <Chip
                    label={`${stats.partial} à moitié clôturé${stats.partial > 1 ? 's' : ''}`}
                    size="small"
                    onClick={() => basculer('partiels')}
                    sx={styleFiltre('partiels', 'sky')}
                  />
                </Tooltip>
              )}
              {stats.orphans > 0 && (
                <Tooltip title="Lignes rattachées à la formation entière : hors décompte, elles ne relèvent d’aucune session. Cliquez pour les afficher">
                  <Chip
                    label={`${stats.orphans} sans session`}
                    size="small"
                    onClick={() => basculer('orphelines')}
                    sx={styleFiltre('orphelines', 'violet')}
                  />
                </Tooltip>
              )}
            </Card>
          )}

          {/* ── Liste ─────────────────────────────────────────────── */}
          {loading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 12 }}>
              <CircularProgress sx={{ color: accent }} />
            </Box>
          )}

          {!loading && filtre === 'orphelines' && (
            <Card sx={{ mb: 2, borderRadius: '18px', border: `1px solid ${grey(100)}`, boxShadow: 'none', overflow: 'hidden' }}>
              <Box sx={{ p: 2.25, bgcolor: toneAlpha('violet', 0.06), borderBottom: `1px solid ${grey(100)}` }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                  Lignes rattachées à la formation entière
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 700, mt: 0.35 }}>
                  Elles ne relèvent d’aucune session : hors du décompte, mais pas hors du suivi.
                </Typography>
              </Box>

              {orphelinesVues.length === 0 && (
                <Typography sx={{ p: 3, textAlign: 'center', color: 'text.disabled', fontSize: '0.85rem' }}>
                  Aucune ligne ne correspond à la recherche.
                </Typography>
              )}

              {orphelinesVues.map((row) => (
                <Box
                  key={row.id}
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${grey(100)}`,
                    opacity: row.closed ? 0.6 : 1,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap'
                  }}
                >
                  <Chip
                    label={row.theme || '—'}
                    size="small"
                    sx={{ bgcolor: toneAlpha('violet', 0.12), color: getTone('violet').text, fontWeight: 700, borderRadius: '8px' }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                      {row.improvement_point || 'Point d’amélioration non renseigné'}
                    </Typography>
                    {row.action_taken && (
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.4 }}>
                        {row.action_taken}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={row.closed ? `Clôturée le ${row.closed_at}` : 'Non clôturée'}
                    size="small"
                    sx={{
                      bgcolor: row.closed ? toneAlpha('emerald', 0.14) : toneAlpha('amber', 0.16),
                      color: row.closed ? getTone('emerald').text : getTone('amber').text,
                      fontWeight: 700,
                      borderRadius: '8px'
                    }}
                  />
                  {row.formation_id ? (
                    <Button
                      component={RouterLink}
                      to={`/dashboard/formation/${row.formation_id}`}
                      size="small"
                      sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800 }}
                    >
                      Ouvrir la formation
                    </Button>
                  ) : null}
                </Box>
              ))}
            </Card>
          )}

          {!loading && filtre !== 'orphelines' && filtered.length === 0 && (
            <Card sx={{ py: 10, textAlign: 'center', borderRadius: '20px', border: `1px solid ${grey(100)}`, boxShadow: 'none' }}>
              <AssignmentIcon sx={{ fontSize: 44, color: grey(300), mb: 1.5 }} />
              <Typography sx={{ fontWeight: 800, color: 'text.secondary', mb: 0.5 }}>
                {sessions.length === 0
                  ? 'Toutes les sessions ont clôturé leur plan d’action'
                  : (filtre !== 'tous'
                    ? 'Aucune session dans ce filtre'
                    : 'Aucune session ne correspond à la recherche')}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>
                {sessions.length === 0
                  ? 'Rien à suivre pour le moment.'
                  : (filtre !== 'tous'
                    ? 'Recliquez sur le compteur pour revoir la liste entière.'
                    : 'Essayez un autre nom de formation ou un autre numéro de session.')}
              </Typography>
            </Card>
          )}

          {!loading && filtered.map((session) => (
            <Card
              key={session.session_id}
              sx={{
                mb: 2,
                borderRadius: '18px',
                border: `1px solid ${grey(100)}`,
                boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
                overflow: 'hidden'
              }}
            >
              {/* Bandeau session : l'identité, l'avancement, les deux liens. */}
              <Box
                sx={{
                  p: 2.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  bgcolor: toneAlpha('amber', 0.06),
                  borderBottom: `1px solid ${grey(100)}`
                }}
              >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.35 }}>
                    {session.formation_name || 'Formation inconnue'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 700, mt: 0.35 }}>
                    {`Session n° ${session.session_number ?? '—'}`}
                    {` · ${formatDateRange(session.session_start_date, session.session_end_date)}`}
                    {session.formation_format ? ` · ${session.formation_format}` : ''}
                  </Typography>
                </Box>

                {/* Trois situations distinctes : rien d'écrit, écrit mais rien
                    de clôturé, ou clôturé à moitié. */}
                <Chip
                  label={stateLabel(session)}
                  size="small"
                  sx={{
                    bgcolor: session.state === 'none' ? toneAlpha('rose', 0.14) : toneAlpha('amber', 0.18),
                    color: session.state === 'none' ? getTone('rose').text : getTone('amber').text,
                    fontWeight: 800,
                    borderRadius: '10px'
                  }}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    component={RouterLink}
                    to={`/dashboard/session/${session.session_id}`}
                    size="small"
                    variant="outlined"
                    startIcon={<EventNoteIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      borderRadius: '10px', textTransform: 'none', fontWeight: 800,
                      borderColor: 'divider', color: 'text.primary', whiteSpace: 'nowrap'
                    }}
                  >
                    Fiche session
                  </Button>
                  <Tooltip title="Ouvrir le plan d’action de la formation, où la clôture se saisit">
                    <Button
                      component={RouterLink}
                      to={`/dashboard/plan-action/${session.formation_id}?type=${encodeURIComponent(session.rows[0]?.theme || 'Satisfaction')}`}
                      disabled={!session.formation_id}
                      size="small"
                      variant="contained"
                      startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        borderRadius: '10px', textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap',
                        background: `linear-gradient(135deg, ${getTone('amber').light} 0%, ${getTone('amber').base} 100%)`,
                        boxShadow: `0 4px 12px ${accent}4D`
                      }}
                    >
                      Plan d’action
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              {/* Une session couverte sans aucune ligne : le plan reste à
                  écrire, et c'est l'essentiel de ce qui manque. */}
              {session.rows.length === 0 && (
                <Box sx={{ p: 2.25, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: grey(300) }} />
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', fontWeight: 600 }}>
                    Aucune ligne de plan d’action n’a encore été écrite pour cette session.
                  </Typography>
                </Box>
              )}

              {/* Les lignes du plan de cette session, clôturées ou non. */}
              {session.rows.map((row) => {
                const themeConfig = QUESTIONNAIRE_TYPES.find((t) => t.value === row.theme);

                return (
                  <Box
                    key={row.id}
                    sx={{
                      p: 2.25,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                      borderBottom: `1px solid ${grey(50)}`,
                      opacity: row.closed ? 0.6 : 1,
                      '&:last-of-type': { borderBottom: 'none' }
                    }}
                  >
                    <Tooltip title={row.closed ? `Clôturée le ${row.closed_at}` : 'Non clôturée'}>
                      <Box sx={{ display: 'flex', pt: 0.25 }}>
                        {row.closed
                          ? <CheckCircleIcon sx={{ fontSize: 20, color: getTone('emerald').base }} />
                          : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: grey(300) }} />}
                      </Box>
                    </Tooltip>

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.75 }}>
                        <Chip
                          label={row.theme || '—'}
                          size="small"
                          sx={{
                            bgcolor: themeConfig ? `${themeConfig.accent}1A` : toneAlpha('neutral', 0.12),
                            color: themeConfig ? themeConfig.accent : getTone('neutral').color,
                            fontWeight: 800, fontSize: '0.63rem', height: 20
                          }}
                        />
                        {row.closed && (
                          <Typography sx={{ fontSize: '0.7rem', color: getTone('emerald').base, fontWeight: 800 }}>
                            {`Clôturée · ${row.closed_at}`}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Point à améliorer
                          </Typography>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
                            {row.improvement_point || '—'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Action mise en place
                          </Typography>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
                            {row.action_taken || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Card>
          ))}
        </Container>
      </Box>
    </>
  );
};

export default ActionPlansOuverts;
